import { Component, OnInit, ViewChild, ElementRef, Input, Output, EventEmitter, AfterViewInit, HostListener, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { MatToolbar } from '@angular/material/toolbar';
import { MatIcon } from '@angular/material/icon';
import { MatCard, MatCardContent, MatCardHeader } from '@angular/material/card';
import { NgClass, NgForOf, NgIf, NgSwitch, NgSwitchCase, TitleCasePipe } from '@angular/common';
import { MatDivider } from '@angular/material/divider';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatTooltip } from '@angular/material/tooltip';
import { DataService, Card } from '../../../services/data.service';
import { finalize } from 'rxjs/operators';
import { FontSizeService, FontSizeOption } from '../../../services/font-size.service';
import { RoleService } from '../../../services/role.service';
import { NavigationPathService, NavigationPathItem } from '../../../services/navigation-path.service';
import { ChangeDetectorRef } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-main-area',
  standalone: true,
  imports: [
    MatToolbar,
    MatIcon,
    MatCard,
    MatCardHeader,
    MatCardContent,
    NgClass,
    NgIf,
    NgSwitch,
    NgSwitchCase,
    TitleCasePipe,
    MatDivider,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    NgForOf,
    MatButton,
    MatIconButton,
    FormsModule,
    ScrollingModule,
    MatProgressSpinnerModule,
    MatCheckbox,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatSlideToggle,
    MatButtonToggleModule,
    MatTooltip
  ],
  templateUrl: './main-area.component.html',
  styleUrl: './main-area.component.css'
})
export class MainAreaComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  // Inputs from parent component
  @Input() p1Mode: 'topicwise' | 'yearwise' = 'topicwise';
  @Input() selectedSubject: string | null = null;
  @Input() selectedSubItem: string | null = null;
  @Input() selectedTopic: string | null = null;
  @Input() selectedSubTopic: string | null = null;
  @Input() selectedYearwiseExam: string | null = null;
  @Input() selectedYearwiseLower: string | null = null;
  @Input() selectedYear: string | null = null;
  @Input() loadedQuestions: Card[] = [];
  @Input() isLoadingQuestions = false;
  @Input() isMobileView = false;
  @Input() showDetails = true; // Add explicit input for showDetails

  // Navigation path expansion state for mobile view
  isNavPathExpanded = false;

  // Loading and error states
  hasQuestionsError = false;
  questionsErrorMessage = '';

  // Cards data
  allCards: Card[] = [];
  displayedCards: Card[] = [];
  selectedCard: Card | null = null;

  // Display state
  allExpanded = true;

  // Display mode options
  displayModes = [
    { value: 'allAtOnce', label: 'Load All Questions (At Once)' },
    { value: 'lazyLoad', label: 'Load All Questions (Lazy)' },
    { value: 'paginated10', label: 'Paginated (10 per page)' },
    { value: 'single', label: 'Single Question View' }
  ];
  displayMode: string = 'allAtOnce'; // Default

  // Language selection
  languageOptions = [

    { value: 'englishOnly', label: 'English Only' },
    { value: 'bilingual', label: 'Bilingual' },
    { value: 'hindiOnly', label: 'Hindi Only' }
  ];
  selectedLanguage: string = 'englishOnly'; // Default

  // Content type selection (kept for compatibility, defaults to imageOnly since OCR text moved to Details pane)
  selectedContentType: string = 'imageOnly';

  // Bilingual layout: side-by-side or stacked (one-after-other)
  bilingualLayout: 'sideBySide' | 'stacked' = 'stacked';

  // Image size: S, M, L, XL
  imageSize: 'S' | 'M' | 'L' | 'XL' = 'M';
  imageSizeOptions: { value: 'S' | 'M' | 'L' | 'XL'; label: string }[] = [
    { value: 'S', label: 'S' },
    { value: 'M', label: 'M' },
    { value: 'L', label: 'L' },
    { value: 'XL', label: 'XL' }
  ];

  // Verification mode (admin only)
  isAdmin = false;
  verificationMode = false;
  showLinkingArrows = true;
  showExplanation = false;
  questionLinks: Map<number, number> = new Map(); // englishIdx → hindiIdx
  arrowPaths: { x1: number; y1: number; x2: number; y2: number; englishIdx: number; hindiIdx: number }[] = [];
  showThumbnailPicker = false;
  thumbnailPickerSourceIndex: number | null = null;
  thumbnailPickerLanguage: 'english' | 'hindi' = 'english';
  private savedShowDetails = true;

  // Toggle options
  showCorrectAnswer: boolean = false;
  optionsLayoutVertical: boolean = false;

  // Pagination
  pageSize = 10;
  currentPage = 1;
  totalPages = 1;

  // Lazy loading
  lazyBatchSize = 3;
  lazyLoadedCount = 0;
  isLoadingMoreLazy = false;

  // Single view
  currentSingleIndex = 0;

  // Page input validation
  invalidPageInput = false;

  // Outputs to parent component
  @Output() selectedCardChange = new EventEmitter<Card | null>();
  @Output() showDetailsChange = new EventEmitter<boolean>();
  @Output() viewModeChange = new EventEmitter<string>();
  @Output() languageChange = new EventEmitter<string>();
  @Output() toggleMobileNav = new EventEmitter<void>();

  // Update property to remove 'main' option
  viewMode: 'split' | 'fullscreen' = 'split';

  @ViewChild('paneContent') paneContent!: ElementRef;

  // Add these properties at the class level, near other component properties
  private lastApiCallParams?: { mode: string, param: string };
  private apiDebounceTimer?: any;
  private readonly API_DEBOUNCE_TIME = 300; // ms

  // Add a Subject for subscription management
  private destroy$ = new Subject<void>();

  // Add currentFontSize property
  currentFontSize: FontSizeOption = 'medium';

  // Add property to store navigation path from service
  navigationPathItems: NavigationPathItem[] = [];

  constructor(
    private dataService: DataService,
    private fontSizeService: FontSizeService,
    private navigationPathService: NavigationPathService,
    private cdr: ChangeDetectorRef,
    private roleService: RoleService
  ) {}

  ngOnInit(): void {
    // Subscribe to font size changes
    this.fontSizeService.fontSize$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(fontSize => {
      this.currentFontSize = fontSize;
      this.cdr.detectChanges();
    });

    // Subscribe to navigation path changes
    this.navigationPathService.navigationPath$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(pathItems => {
      this.navigationPathItems = pathItems;
      this.cdr.detectChanges();
    });

    // Check if user is admin
    this.roleService.currentRole$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(role => {
      this.isAdmin = role === 'admin';
    });

    // Get the current navigation path immediately without waiting for first emission
    this.navigationPathService.getCurrentPath().subscribe((path: NavigationPathItem[]) => {
      if (path && path.length > 0) {
        this.navigationPathItems = path;
        this.cdr.detectChanges();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('MainAreaComponent -> ngOnChanges');

    // Check for showDetails changes coming from parent
    if (changes['showDetails']) {
      console.log('showDetails changed:', changes['showDetails'].currentValue);
      // No need to emit an event since the change came from the parent
    }

    // Check if loadedQuestions changed and update if needed
    if (changes['loadedQuestions']) {
      console.log('loadedQuestions changed:', changes['loadedQuestions'].currentValue);
      this.updateCards();

      // When questions are loaded, select the first question by default in desktop mode
      if (this.allCards && this.allCards.length > 0 && !this.isMobileView) {
        this.selectedCard = this.allCards[0];
        // Emit the selected card to show the details area
        this.selectedCardChange.emit(this.selectedCard);
      }
    }

    // Check if isLoadingQuestions changed
    if (changes['isLoadingQuestions']) {
      console.log('isLoadingQuestions changed:', changes['isLoadingQuestions'].currentValue);
      if (!this.isLoadingQuestions && this.displayMode) {
        // If loading is complete and we have a display mode, re-apply it
        this.applyDisplayMode();
      }
    }

    // Handle mobile view changes
    if (changes['isMobileView']) {
      // If switching to mobile view, we need to ensure the internal state is correct
      if (this.isMobileView) {
        // In mobile view:
        // 1. Don't show the "Show Details" checkbox
        // 2. Still maintain the showDetails value for when returning to desktop
        // 3. Emit the change so parent component knows details should be shown separately in mobile

      } else {
        // When returning to desktop view, emit the stored value to restore previous state
        this.showDetailsChange.emit(this.showDetails);
      }
    }

    // Clear any pending API calls when new selection changes are made
    if (this.apiDebounceTimer) {
      clearTimeout(this.apiDebounceTimer);
      this.apiDebounceTimer = undefined;
    }

    // Handle directly loaded questions from parent component
    if (changes['loadedQuestions']) {
      // Log the change for debugging
      console.log('loadedQuestions changed:', this.loadedQuestions,
        'length:', this.loadedQuestions?.length || 0);

      // Check parent's loading state - don't update if loading to prevent flickering
      if (this.isLoadingQuestions) {
        console.log('Questions are still loading, preserving current display state');
        // Don't update cards or clear anything while loading
        return; // Skip processing while loading
      }

      // Set allCards to loadedQuestions (even if empty)
      this.allCards = this.loadedQuestions || [];

      // Apply the display mode (this will also handle empty arrays properly)
      this.applyDisplayMode();

      // Only show error if we truly have no questions AND we're not in a loading state
      if (Array.isArray(this.loadedQuestions) && this.loadedQuestions.length === 0 && !this.isLoadingQuestions) {
        console.log('No questions to display from parent component');
        this.hasQuestionsError = true;
        this.questionsErrorMessage = `No questions available for the selected topic.`;
      } else if (this.loadedQuestions && this.loadedQuestions.length > 0) {
        console.log(`Displaying ${this.loadedQuestions.length} questions from parent component`);
        // Make sure error state is cleared if we have questions
        this.hasQuestionsError = false;
        this.questionsErrorMessage = '';
      }

      return; // Skip API calls as we've already updated the UI
    }

    this.apiDebounceTimer = setTimeout(() => {
      console.log('Debounced changes processing starting', new Date().toISOString());
      console.log('Current mode:', this.p1Mode);

      // For topicwise mode, ONLY call loadQuestions when subtopic is selected
      if (this.p1Mode === 'topicwise') {
        // Check if selectedSubTopic has changed and has a valid value
        if (changes['selectedSubTopic'] && this.selectedSubTopic) {
          // Skip empty subtopics
          if (!this.selectedSubTopic.trim()) {
            console.log('Skipping API call - empty subtopic');
            return;
          }

          // Skip duplicate calls
          const apiParams = { mode: 'topicwise', param: this.selectedSubTopic };
          if (this.lastApiCallParams &&
            this.lastApiCallParams.mode === apiParams.mode &&
            this.lastApiCallParams.param === apiParams.param) {
            console.log('Skipping duplicate API call for same subtopic:', this.selectedSubTopic);
            return;
          }

          console.log('Selected subtopic changed to:', this.selectedSubTopic);
          console.log('Making API call for subtopic:', this.selectedSubTopic);
          this.lastApiCallParams = apiParams;
          this.loadQuestions(this.selectedSubTopic);
        } else {
          // Do not load questions for subject/section/topic changes
          console.log('Skipping API call - waiting for subtopic selection in topicwise mode');
        }
      }

      // For yearwise mode, ONLY call loadQuestions when year is selected
      else if (this.p1Mode === 'yearwise') {
        // Only load questions if ALL required yearwise selections are made AND year specifically changed
        if (this.selectedYearwiseExam &&
          this.selectedYearwiseLower &&
          this.selectedYear &&
          (changes['selectedYear'] || changes['selectedYearwiseLower'] || changes['selectedYearwiseExam'])) {

          const yearwiseQuery = `${this.selectedYearwiseExam}-${this.selectedYearwiseLower}-${this.selectedYear}`;

          // Check if we're trying to load the same parameters again
          const apiParams = { mode: 'yearwise', param: yearwiseQuery };

          if (this.lastApiCallParams &&
            this.lastApiCallParams.mode === apiParams.mode &&
            this.lastApiCallParams.param === apiParams.param) {
            console.log('Skipping duplicate API call for same yearwise query:', yearwiseQuery);
            return;
          }

          console.log('Year selected. Loading yearwise questions with query:', yearwiseQuery);
          this.lastApiCallParams = apiParams;
          this.loadQuestions(yearwiseQuery);
        } else {
          // Do not load questions for exam/lower selections
          console.log('Skipping API call - waiting for year selection in yearwise mode');
        }
      }
    }, this.API_DEBOUNCE_TIME);
  }

  ngAfterViewInit() {
    // Apply custom class to select panel
    setTimeout(() => {
      const panelClass = document.querySelector('.mat-mdc-select-panel');
      if (panelClass) {
        panelClass.classList.add('modern-select-panel');
      }
    }, 0);
  }

  // Load questions for a subtopic
  loadQuestions(subtopic: string): void {
    if (!subtopic) {
      console.log('Cannot load questions: No subtopic provided');
      return;
    }

    // Prevent duplicate API calls for the same parameters
    const apiParams = {mode: this.p1Mode, param: subtopic};
    if (this.lastApiCallParams &&
      this.lastApiCallParams.mode === apiParams.mode &&
      this.lastApiCallParams.param === apiParams.param) {
      console.log('Skipping duplicate questions API call');
      return;
    }

    // Clear previous timer if exists
    if (this.apiDebounceTimer) {
      clearTimeout(this.apiDebounceTimer);
    }

    // Add debounce to avoid rapid successive API calls
    this.apiDebounceTimer = setTimeout(() => {
      this.lastApiCallParams = apiParams;
      this._loadQuestionsImpl(subtopic);
    }, this.API_DEBOUNCE_TIME);
  }

  private _loadQuestionsImpl(subtopic: string): void {
    console.log(`Loading questions for subtopic: '${subtopic}'`);

    // Set local loading indicator (additive to parent's state)
    this.dataService.getQuestions(subtopic)
      .pipe(
        finalize(() => {
          // We don't clear loading state here as it's controlled by parent
        })
      )
      .subscribe({
        next: (questions) => {
          // Ensure questions is treated as an array
          if (!Array.isArray(questions)) {
            console.error(`API returned non-array response for '${subtopic}'`, questions);
            this.allCards = [];
            this.hasQuestionsError = true;
            this.questionsErrorMessage = `Received invalid data for '${subtopic}'.`;
          } else {
            this.allCards = questions;

            // Set error state only if array is empty
            if (questions.length === 0) {
              this.hasQuestionsError = true;
              this.questionsErrorMessage = `No questions available for '${subtopic}'.`;
              console.log(`ℹ️ No questions found for '${subtopic}'`);
            } else {
              // Clear error state if we have questions
              this.hasQuestionsError = false;
              this.questionsErrorMessage = '';
              console.log(`✅ API SUCCESS: Received ${questions.length} questions for '${subtopic}'`);
            }
          }

          // Always apply display mode to update UI
          this.applyDisplayMode();
        },
        error: (error) => {
          // Handle API errors
          this.hasQuestionsError = true;
          this.questionsErrorMessage = `Failed to load questions.`;
          console.error(`❌ Error loading questions for '${subtopic}':`, error);

          // Set empty cards array when API fails
          this.allCards = [];
          this.applyDisplayMode();
          console.log(`📝 Set empty questions list for '${subtopic}' due to API error`);
        }
      });
  }

  // ─────────────────────────────────────────────────────────
  // Handling the drop-down choice
  // ─────────────────────────────────────────────────────────
  onDisplayModeChange(): void {
    // Reset any state needed and re-apply the selected mode
    this.currentPage = 1;
    this.invalidPageInput = false; // Reset invalid page input state
    this.applyDisplayMode();
  }

  applyDisplayMode(): void {
    this.selectedCard = null;   // Reset selected
    // Emit null selected card to the parent to clear details area
    this.selectedCardChange.emit(null);

    // Reset expansions
    this.allExpanded = true;
    // Reset lazy loading state
    this.isLoadingMoreLazy = false;

    // Log the current state for debugging
    console.log(`applyDisplayMode: allCards has ${this.allCards?.length || 0} questions`);

    // Don't show error when still loading
    if (this.isLoadingQuestions) {
      console.log('Questions are still loading, not showing error yet');
      return;
    }

    // Make sure allCards is populated
    if (!this.allCards || this.allCards.length === 0) {
      console.log('No cards to display (allCards is empty)');
      this.displayedCards = [];
      // Make sure we clear the selected card when there are no questions
      this.selectedCard = null;
      this.selectedCardChange.emit(null);
      return; // No cards to display
    }

    console.log(`Applying display mode: ${this.displayMode} for ${this.allCards.length} cards`);

    // Set the first card as selected by default only in desktop mode and emit it
    if (this.allCards.length > 0 && !this.isMobileView) {
      this.selectedCard = this.allCards[0];
      // Emit the first card by default only in desktop mode
      this.selectedCardChange.emit(this.selectedCard);

      // Always show details when selecting first card in desktop mode
      this.showDetails = true;
      this.showDetailsChange.emit(true);
    }

    switch (this.displayMode) {
      case 'allAtOnce':
        // Load everything at once - make a complete copy to ensure all items are displayed
        this.displayedCards = [...this.allCards];
        console.log(`allAtOnce mode - displaying all ${this.displayedCards.length} cards`);
        break;

      case 'lazyLoad':
        // Start with more items initially for better UX
        this.lazyBatchSize = 20; // Increase batch size for better initial experience
        this.lazyLoadedCount = Math.min(this.lazyBatchSize, this.allCards.length);
        this.displayedCards = this.allCards.slice(0, this.lazyLoadedCount);
        console.log(`Initial lazy load: ${this.lazyLoadedCount} of ${this.allCards.length} questions`);

        // Force a check for more content after a short delay
        // This helps if the initial content doesn't fill the viewport
        setTimeout(() => {
          this.checkForMoreContent();
        }, 1000);
        break;

      case 'paginated10':
        this.pageSize = 10; // Show 10 items per page to match name
        this.currentPage = 1;
        this.setupPagination();
        console.log(`Paginated mode - page size: ${this.pageSize}, total items: ${this.allCards.length}, total pages: ${this.totalPages}`);
        break;

      case 'single':
        // Show first question
        this.currentSingleIndex = 0;
        if (this.allCards.length > 0) {
          this.displayedCards = [this.allCards[this.currentSingleIndex]];
          // Log the question text for debugging
          console.log(`Single view mode - showing 1 of ${this.allCards.length} questions`);

          // Allow time for the DOM to update, then adjust layout
          setTimeout(() => {
            this.adjustSingleViewLayout();
          }, 100);
        } else {
          this.displayedCards = [];
        }
        break;
    }

    // Apply language mode
    this.applyLanguageMode();
    // Apply content type
    this.applyContentTypeMode();
  }

  // New method to adjust the layout in single view mode
  adjustSingleViewLayout(): void {
    if (this.displayMode !== 'single') return;

    // Log that we're adjusting the layout
    console.log('Adjusting single view layout');

    // Get references to the elements
    if (this.paneContent && this.paneContent.nativeElement) {
      setTimeout(() => {
        const singleView = this.paneContent.nativeElement.querySelector('.single-question-view');
        if (singleView) {
          const textPortion = singleView.querySelector('.text-portion');
          const imagePortion = singleView.querySelector('.image-portion');
          const paneFooter = this.paneContent.nativeElement.querySelector('.pane-footer');

          if (textPortion && imagePortion && paneFooter) {
            // Get the available height
            const totalHeight = singleView.clientHeight;
            const textHeight = textPortion.scrollHeight;
            const footerHeight = paneFooter.clientHeight;
            const navigationHeight = singleView.previousElementSibling ? singleView.previousElementSibling.clientHeight : 0;

            // Calculate the remaining height for the image, accounting for margins and padding
            const availableHeight = totalHeight - textHeight - 32; // 32px for margins/padding
            const imageHeight = Math.max(200, availableHeight);

            // Log the measurements for debugging
            console.log(`Single view layout: Total height=${totalHeight}px, Text height=${textHeight}px, Footer height=${footerHeight}px, Nav height=${navigationHeight}px, Available height=${availableHeight}px, Image height=${imageHeight}px`);

            // Apply the calculated height to the image portion
            imagePortion.style.height = `${imageHeight}px`;

            // Ensure the image fills the available space
            const img = imagePortion.querySelector('img');
            if (img) {
              // Force the image to redraw at the new size
              img.style.objectFit = 'contain';
            }
          }
        }
      }, 100); // Small delay to ensure DOM is updated
    }
  }

  // ─────────────────────────────────────────────────────────
  // Lazy Loading: detect scroll at bottom
  // ─────────────────────────────────────────────────────────
  @HostListener('scroll', ['$event'])
  onPaneScroll(e: Event) {
    // If not in lazyLoad mode, do nothing
    if (this.displayMode !== 'lazyLoad') return;

    this.checkForMoreContent();
  }

  // Separate method to check if we need to load more content
  // This can be called from scroll event or programmatically
  checkForMoreContent(): void {
    // If not in lazyLoad mode or already loading, do nothing
    if (this.displayMode !== 'lazyLoad' || this.isLoadingMoreLazy) return;

    // Use the paneContent reference directly
    if (this.paneContent && this.paneContent.nativeElement) {
      const element = this.paneContent.nativeElement;
      const scrollableContent = element.querySelector('.pane-content');

      if (scrollableContent) {
        const threshold = scrollableContent.scrollHeight - scrollableContent.scrollTop - scrollableContent.clientHeight;

        console.log(`Scroll detected: ${threshold}px from bottom, scrollHeight: ${scrollableContent.scrollHeight}, scrollTop: ${scrollableContent.scrollTop}, clientHeight: ${scrollableContent.clientHeight}`);

        // If near the bottom (within 500px), load next batch
        if (threshold < 500 && !this.isLoadingMoreLazy) {
          console.log('Near bottom, loading more items...');
          this.loadMoreLazy();
        }
      } else {
        console.warn('Could not find scrollable content element');
      }
    }
  }

  loadMoreLazy(): void {
    // Add next batch if available
    if (this.lazyLoadedCount < this.allCards.length && !this.isLoadingMoreLazy) {
      this.isLoadingMoreLazy = true;
      console.log('Loading more items started...');

      // Simulate network delay for smoother UX
      setTimeout(() => {
        // Calculate how many more items to load - load more at once for better UX
        const remainingItems = this.allCards.length - this.lazyLoadedCount;
        const itemsToLoad = Math.min(20, remainingItems); // Load 20 items at a time for better performance

        this.lazyLoadedCount += itemsToLoad;
        this.displayedCards = this.allCards.slice(0, this.lazyLoadedCount);

        console.log(`Lazy loaded ${this.lazyLoadedCount} of ${this.allCards.length} questions (added ${itemsToLoad} more)`);

        // Keep the loading indicator visible a bit longer for better UX
        setTimeout(() => {
          this.isLoadingMoreLazy = false;

          // Check if we need to load more after a short delay
          // This helps if the loaded content doesn't fill the viewport
          setTimeout(() => {
            this.checkForMoreContent();
          }, 300);
        }, 300);
      }, 500); // Shorter delay for better responsiveness
    }
  }

  // ─────────────────────────────────────────────────────────
  // Pagination (10/page) or single
  // ─────────────────────────────────────────────────────────
  setupPagination(): void {
    // For 10/page mode
    if (this.displayMode === 'paginated10') {
      this.totalPages = Math.ceil(this.allCards.length / this.pageSize);
      this.showCurrentPage();
    }
  }

  showCurrentPage(): void {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.displayedCards = this.allCards.slice(startIndex, endIndex);

    // We only set the selectedCard in desktop mode when initially loading
    // but don't emit unless specifically clicked by the user
    if (this.displayedCards.length > 0 && !this.isMobileView && this.selectedCard === null) {
      this.selectedCard = this.displayedCards[0];
      // We don't emit here to avoid showing details automatically when changing pages
    }
  }

  goToPreviousPage(): void {
    if (this.displayMode === 'paginated10') {
      if (this.currentPage > 1) {
        this.currentPage--;
        const startIndex = (this.currentPage - 1) * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        this.displayedCards = this.allCards.slice(startIndex, endIndex);

        // Don't automatically select any card when changing pages
        // Let user click to select which question to view in details area
      }
    } else if (this.displayMode === 'single') {
      if (this.currentSingleIndex > 0) {
        this.currentSingleIndex--;
        this.displayedCards = [ this.allCards[this.currentSingleIndex] ];

        // Don't automatically emit the card selection - let user click to view details
        // User must explicitly click on the question to see details

        // Adjust layout after changing the question
        setTimeout(() => {
          this.adjustSingleViewLayout();
        }, 100);
      }
    }
  }

  goToNextPage(): void {
    if (this.displayMode === 'paginated10') {
      const totalPages = Math.ceil(this.allCards.length / this.pageSize);
      if (this.currentPage < totalPages) {
        this.currentPage++;
        const startIndex = (this.currentPage - 1) * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        this.displayedCards = this.allCards.slice(startIndex, endIndex);

        // Don't automatically select any card when changing pages
        // Let user click to select which question to view in details area
      }
    } else if (this.displayMode === 'single') {
      if (this.currentSingleIndex < this.allCards.length - 1) {
        this.currentSingleIndex++;
        this.displayedCards = [ this.allCards[this.currentSingleIndex] ];

        // Don't automatically emit the card selection - let user click to view details
        // User must explicitly click on the question to see details

        // Adjust layout after changing the question
        setTimeout(() => {
          this.adjustSingleViewLayout();
        }, 100);
      }
    }
  }

  goToPage(page: number): void {
    // Reset invalid state at the beginning
    this.invalidPageInput = false;

    // Ensure page is a valid number
    if (isNaN(page)) {
      this.invalidPageInput = true;
      return;
    }

    // If user types in the page number box
    if (this.displayMode === 'paginated10') {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        const startIndex = (this.currentPage - 1) * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        this.displayedCards = this.allCards.slice(startIndex, endIndex);

        // Update the selected card for internal tracking but don't emit
        if (this.displayedCards.length > 0) {
          this.selectedCard = this.displayedCards[0];
          // We don't emit here to avoid showing details: this.selectedCardChange.emit(this.selectedCard);
        }
      } else {
        this.invalidPageInput = true;
        console.warn(`Invalid page number: ${page}. Valid range: 1-${this.totalPages}`);
      }
    } else if (this.displayMode === 'single') {
      // For single view, page number is the question index (1-indexed)
      if (page >= 1 && page <= this.allCards.length) {
        this.currentSingleIndex = page - 1; // Convert to 0-indexed
        this.displayedCards = [this.allCards[this.currentSingleIndex]];
        // Update the selected card for internal tracking but don't emit
        this.selectedCard = this.allCards[this.currentSingleIndex];
        // We don't emit here to avoid showing details: this.selectedCardChange.emit(this.selectedCard);
        console.log(`Navigated to question ${page} in single view mode`);

        // Adjust layout after changing the question
        setTimeout(() => {
          this.adjustSingleViewLayout();
        }, 100);
      } else {
        this.invalidPageInput = true;
        console.warn(`Invalid question number: ${page}. Valid range: 1-${this.allCards.length}`);
      }
    }
  }

  // ─────────────────────────────────────────────────────────
  // Card expansions + selection for Pane 4
  // ─────────────────────────────────────────────────────────
  toggleAllCards() {
    this.allExpanded = !this.allExpanded;

    // Only apply to cards if not in single view mode
    if (this.displayMode !== 'single') {
      this.displayedCards.forEach(card => card.collapsed = !this.allExpanded);
    }
  }

  toggleCard(card: Card) {
    card.collapsed = !card.collapsed;
  }

  // Select a card (question)
  selectCard(card: Card): void {
    console.log('Card selected:', card);

    // Update selected card
    this.selectedCard = card;

    // Always set showDetails to true when a card is selected
    this.showDetails = true;

    // First emit the showDetails change to ensure the details area is visible
    this.showDetailsChange.emit(true);

    // Then emit the card selection after a small delay to ensure details area is ready
    setTimeout(() => {
      this.selectedCardChange.emit(card);

      // Try to scroll to the details area component
      setTimeout(() => {
        const detailsElement = document.querySelector('app-details-area');
        if (detailsElement) {
          console.log('Scrolling to details area after card selection');
          detailsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }, 50);
  }

  // Open a dialog to jump to a specific page
  openPageDialog(): void {
    const maxPage = this.displayMode === 'single' ? this.allCards.length : this.totalPages;
    const currentPage = this.displayMode === 'single' ? this.currentSingleIndex + 1 : this.currentPage;

    // Use browser's prompt for simplicity
    const input = prompt(`Enter page number (1-${maxPage}):`, currentPage.toString());

    if (input !== null) {
      const page = parseInt(input, 10);
      if (!isNaN(page)) {
        this.goToPage(page);
      }
    }
  }

  // Method to handle language selection change
  onLanguageChange(): void {
    console.log('Language mode changed to:', this.selectedLanguage);
    this.applyLanguageMode();
    this.languageChange.emit(this.selectedLanguage.toLowerCase());
  }

  // Method to handle content type selection change
  onContentTypeChange(): void {
    console.log('Content type changed to:', this.selectedContentType);
    this.applyContentTypeMode();
  }

  // Apply the selected language mode
  applyLanguageMode(): void {
    // Implementation will depend on the actual data structure
    // For now, we'll just log the change
    console.log('Applying language mode:', this.selectedLanguage);

    // In a real implementation, this would filter or transform the displayed cards
    // based on the selected language
  }

  // Apply the selected content type mode
  applyContentTypeMode(): void {
    // Implementation will depend on the actual data structure
    // For now, we'll just log the change
    console.log('Applying content type mode:', this.selectedContentType);

    // In a real implementation, this would adjust the display to show
    // images, text, or both based on the selected content type
  }

  onShowDetailsChange(): void {
    this.showDetailsChange.emit(this.showDetails);
  }

  // Get the appropriate icon for display mode
  getDisplayModeIcon(): string {
    switch (this.displayMode) {
      case 'allAtOnce':
        return 'view_list';
      case 'lazyLoad':
        return 'autorenew';
      case 'paginated10':
        return 'view_module';
      case 'single':
        return 'visibility';
      default:
        return 'dashboard';
    }
  }

  // Get the appropriate icon for language selection
  getLanguageIcon(): string {
    switch (this.selectedLanguage) {
      case 'bilingual':
        return 'language';
      case 'englishOnly':
        return 'abc';
      case 'hindiOnly':
        return 'translate';
      default:
        return 'language';
    }
  }

  // Get the appropriate icon for content type selection
  getContentTypeIcon(): string {
    switch (this.selectedContentType) {
      case 'imageAndText':
        return 'view_carousel';
      case 'imageOnly':
        return 'image';
      case 'textOnly':
        return 'text_fields';
      default:
        return 'view_carousel';
    }
  }

  // Get the appropriate icon for the current view mode
  getViewModeIcon(): string {
    switch (this.viewMode) {
      case 'split':
        return 'view_quilt';
      case 'fullscreen':
        return 'fullscreen';
      default:
        return 'view_quilt';
    }
  }

  // Set the view mode and emit change event
  setViewMode(mode: 'split' | 'fullscreen'): void {
    if (this.viewMode !== mode) {
      this.viewMode = mode;
      this.viewModeChange.emit(mode);
    }
  }

  // Add method to toggle mobile navigation
  onToggleMobileNav(): void {
    this.toggleMobileNav.emit();
  }

  toggleShowDetails(): void {
    this.showDetails = !this.showDetails;
    this.onShowDetailsChange();
  }

  // Toggle the navigation path expanded state in mobile view
  toggleNavPathExpanded(): void {
    this.isNavPathExpanded = !this.isNavPathExpanded;
  }

  // ─────────────────────────────────────────────────────────
  // Verification Mode (Admin Only)
  // ─────────────────────────────────────────────────────────
  toggleVerificationMode(): void {
    this.verificationMode = !this.verificationMode;
    if (this.verificationMode) {
      // Hide details pane and use full width
      this.savedShowDetails = this.showDetails;
      this.showDetails = false;
      this.showDetailsChange.emit(false);
      this.initQuestionLinks();
      setTimeout(() => this.updateArrowPositions(), 200);
    } else {
      this.arrowPaths = [];
      this.closeThumbnailPicker();
      // Restore details pane
      this.showDetails = this.savedShowDetails;
      this.showDetailsChange.emit(this.showDetails);
    }
  }

  toggleLinkingArrows(): void {
    this.showLinkingArrows = !this.showLinkingArrows;
    if (this.showLinkingArrows) {
      setTimeout(() => this.updateArrowPositions(), 100);
    } else {
      this.arrowPaths = [];
    }
  }

  initQuestionLinks(): void {
    this.questionLinks.clear();
    this.allCards.forEach((_, i) => {
      this.questionLinks.set(i, i);
    });
  }

  updateArrowPositions(): void {
    if (!this.verificationMode || !this.showLinkingArrows) {
      this.arrowPaths = [];
      return;
    }
    const container = document.querySelector('.verification-columns');
    if (!container) return;

    const englishCards = container.querySelectorAll('.english-column .verification-card');
    const hindiCards = container.querySelectorAll('.hindi-column .verification-card');
    const containerRect = container.getBoundingClientRect();

    const paths: typeof this.arrowPaths = [];
    this.questionLinks.forEach((hindiIdx, englishIdx) => {
      const engCard = englishCards[englishIdx];
      const hinCard = hindiCards[hindiIdx];
      if (engCard && hinCard) {
        const engRect = engCard.getBoundingClientRect();
        const hinRect = hinCard.getBoundingClientRect();
        paths.push({
          x1: engRect.right - containerRect.left,
          y1: engRect.top + engRect.height / 2 - containerRect.top,
          x2: hinRect.left - containerRect.left,
          y2: hinRect.top + hinRect.height / 2 - containerRect.top,
          englishIdx,
          hindiIdx
        });
      }
    });
    this.arrowPaths = paths;
    this.cdr.detectChanges();
  }

  openLinkPicker(cardIndex: number, language: 'english' | 'hindi'): void {
    this.thumbnailPickerSourceIndex = cardIndex;
    this.thumbnailPickerLanguage = language;
    this.showThumbnailPicker = true;
  }

  closeThumbnailPicker(): void {
    this.showThumbnailPicker = false;
    this.thumbnailPickerSourceIndex = null;
  }

  linkQuestion(targetIdx: number): void {
    if (this.thumbnailPickerSourceIndex === null) return;

    if (this.thumbnailPickerLanguage === 'english') {
      // Source is English, link it to the chosen Hindi question
      this.questionLinks.set(this.thumbnailPickerSourceIndex, targetIdx);
    } else {
      // Source is Hindi, find which English card to repoint
      const oldEnglishIdx = this.thumbnailPickerSourceIndex;
      this.questionLinks.set(targetIdx, oldEnglishIdx);
    }
    this.closeThumbnailPicker();
    setTimeout(() => this.updateArrowPositions(), 100);
  }

  onVerificationCardDragStart(event: DragEvent, cardIndex: number, language: 'english' | 'hindi'): void {
    event.dataTransfer?.setData('text/plain', JSON.stringify({ cardIndex, language }));
    event.dataTransfer!.effectAllowed = 'link';
  }

  onVerificationCardDrop(event: DragEvent, targetIndex: number, targetLanguage: 'english' | 'hindi'): void {
    event.preventDefault();
    const data = event.dataTransfer?.getData('text/plain');
    if (!data) return;
    try {
      const { cardIndex, language } = JSON.parse(data);
      if (language === targetLanguage) return;
      if (language === 'english') {
        this.questionLinks.set(cardIndex, targetIndex);
      } else {
        this.questionLinks.set(targetIndex, cardIndex);
      }
      setTimeout(() => this.updateArrowPositions(), 100);
    } catch (e) {
      console.error('Invalid drag data', e);
    }
  }

  onVerificationCardDragOver(event: DragEvent): void {
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'link';
  }

  onVerificationScroll(): void {
    if (this.verificationMode && this.showLinkingArrows) {
      this.updateArrowPositions();
    }
  }

  // Implement ngOnDestroy to clean up subscriptions
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Add updateCards method to process loadedQuestions
  updateCards(): void {
    // Update allCards from loadedQuestions
    this.allCards = this.loadedQuestions ? [...this.loadedQuestions] : [];

    // Apply current display mode to new cards
    if (this.displayMode) {
      this.applyDisplayMode();
    } else {
      // Default to paginated10 if no display mode is set
      this.displayMode = 'paginated10';
      this.applyDisplayMode();
    }

    // Reset pagination
    this.currentPage = 1;
    this.currentSingleIndex = 0;

    console.log(`Updated cards: ${this.allCards.length} questions loaded`);
  }

  // Method to format a node name based on language setting
  formatNodeName(englishName: string, hindiName?: string): string {
    if (!hindiName) return englishName;

    switch (this.selectedLanguage) {
      case 'englishOnly':
        return englishName;
      case 'hindiOnly':
        return hindiName;
      case 'bilingual':
      default:
        return `${englishName} ${hindiName ? `(${hindiName})` : ''}`;
    }
  }

  /**
   * Extract the correct answer letter from a card's answerText.
   * Handles formats like "Ans. (c)", "Ans. (C)", "Ans.(d)", "Ans. (*)"
   */
  getCorrectAnswerLetter(card: Card): string {
    if (!card || !card.answerText) return '';
    const match = card.answerText.match(/\(([a-dA-D*])\)/);
    if (match && match[1]) {
      return match[1].toUpperCase();
    }
    return '';
  }
}
